import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { transitionTo } from "./Transition";

gsap.registerPlugin(ScrollTrigger);

const PAGE_LINKS = [
  { label: "Home", href: "/", testId: "nav-link-home" },
  { label: "About", href: "/about", testId: "nav-link-about" },
  { label: "Businesses", href: "/businesses", testId: "nav-link-businesses" },
  { label: "Capabilities", href: "/capabilities", testId: "nav-link-capabilities" },
  { label: "Contact", href: "/contact", testId: "nav-link-contact" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const barRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const st = ScrollTrigger.create({
      start: 0,
      end: "max",
      onUpdate: (self) => {
        if (barRef.current) gsap.set(barRef.current, { scaleX: self.progress });
      },
    });
    return () => st.kill();
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (href === location.pathname) {
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
      else window.scrollTo(0, 0);
      return;
    }
    transitionTo(navigate, href);
  };

  const isActive = (href) =>
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href);

  return (
    <>
      <div
        ref={barRef}
        className="fixed left-0 top-0 z-[60] h-[2px] w-full origin-left scale-x-0 bg-amber"
        data-testid="nav-scroll-progress"
        aria-hidden="true"
      />
      <header className="fixed top-0 z-50 w-full border-b border-white/10 bg-black/40 backdrop-blur-xl">
        <nav className="flex items-center justify-between px-[6vw] py-4 md:py-5" aria-label="Primary">
          <a
            href="/"
            onClick={(e) => go(e, "/")}
            className="group flex items-baseline gap-2"
            data-testid="nav-brand"
          >
            <span className="font-display text-2xl font-black leading-none tracking-tight transition-colors duration-300 group-hover:text-amber">
              OA
            </span>
            <span className="font-mono text-[10px] tracking-[0.3em] text-smoke">HOLDING</span>
          </a>

          <div className="hidden items-center gap-10 md:flex">
            {PAGE_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={l.testId}
                className={`group relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:text-amber ${
                  isActive(l.href) ? "text-amber" : "text-bone/80"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px w-full origin-left bg-amber transition-transform duration-300 ease-out group-hover:scale-x-100 ${
                    isActive(l.href) ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </a>
            ))}
            <a
              href="/contact"
              onClick={(e) => go(e, "/contact")}
              data-testid="nav-cta"
              className="group flex items-center gap-2 border border-amber/60 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amber transition-colors duration-300 hover:bg-amber hover:text-ink"
            >
              Start a conversation
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>

          <button
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] md:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink px-[8vw] md:hidden"
            data-testid="nav-mobile-overlay"
          >
            <div className="flex flex-col gap-6">
              {PAGE_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`nav-mobile-${l.testId}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className={`font-display text-4xl font-black uppercase tracking-tight ${
                    isActive(l.href) ? "text-amber" : ""
                  }`}
                >
                  <span className="mr-4 font-mono text-xs text-amber">0{i + 1}</span>
                  {l.label}
                </motion.a>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-16 border-t border-white/10 pt-6"
            >
              <a
                href="mailto:info@oaholding.co"
                className="font-mono text-xs tracking-[0.2em] text-smoke"
                data-testid="nav-mobile-email"
              >
                INFO@OAHOLDING.CO
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
